import React from 'react'
import * as XLSX from 'xlsx'


const MappingTable = ({ title, mappings }) => {
  if (!mappings || !Array.isArray(mappings)) return null
  return (
    <div className="mt-4">
      <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-200 dark:bg-gray-700">
              <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-left">Target Column</th>
              <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-left">Source Column</th>
              <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center">Score</th>
              <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center">Confidence</th>
              <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-left">Rationale</th>
            </tr>
          </thead>
          <tbody>
            {mappings.map((mapping, idx) => (
              <tr key={idx} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-750'}>
                <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">{mapping.target_column}</td>
                <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">{mapping.source_column}</td>
                <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center">{mapping.match_score}</td>
                <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center">
                  <span
                    className={
                      mapping.confidence === 'high'
                        ? 'bg-green-200 dark:bg-green-700 px-2 py-0.5 rounded text-gray-800 dark:text-white'
                        : mapping.confidence === 'medium'
                          ? 'bg-yellow-200 dark:bg-yellow-700 px-2 py-0.5 rounded text-gray-800 dark:text-white'
                          : 'bg-red-200 dark:bg-red-700 px-2 py-0.5 rounded text-gray-800 dark:text-white'
                    }
                  >
                    {mapping.confidence}
                  </span>
                </td>
                <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs">{mapping.rationale}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const ReviewPayloadComponent = ({ payload }) => {
  if (!payload) return null
  const { summary, source_sheet_detection, validation, pnl_mappings, census_mappings } = payload
  return (
    <div className="space-y-4 text-sm">
      <div>
        <h3 className="font-semibold text-gray-800 dark:text-gray-200">Summary</h3>
        <p className="text-gray-700 dark:text-gray-300 mt-1">{summary}</p>
      </div>

      {source_sheet_detection && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded p-3">
          <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Source Sheet Detection</h3>
          <div className="space-y-1 text-gray-700 dark:text-gray-300">
            <p>
              <span className="font-medium">P&L Source Sheet:</span> {source_sheet_detection.pnl_source_sheet}
            </p>
            <p>
              <span className="font-medium">Census Source Sheet:</span> {source_sheet_detection.census_source_sheet}
            </p>
          </div>
        </div>
      )}

      {validation?.warnings && validation.warnings.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded p-3">
          <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Warnings</h3>
          <ul className="space-y-2">
            {validation.warnings.map((warning, idx) => (
              <li key={idx} className="text-gray-700 dark:text-gray-300 flex gap-2">
                <span className="text-amber-600 dark:text-amber-400 flex-shrink-0">⚠️</span>
                <span>{warning}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <MappingTable title="P&L Mappings" mappings={pnl_mappings} />
      <MappingTable title="Census Mappings" mappings={census_mappings} />
    </div>
  )
}

const buildAgentUrl = (pathOrUrl) => {
  if (!pathOrUrl) return ''
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl
  return `${DATA_FORMATER_AGENT_HOST}${pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`}`
}

const DownloadArtifactMessage = ({ url }) => {
  const downloadUrl = buildAgentUrl(url)
  if (!downloadUrl) return null

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-700 dark:text-gray-300">Your generated file is ready.</p>
      <a
        href={downloadUrl}
        download
        className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
      >
        Download file
      </a>
    </div>
  )
}

const EXCEL_PREVIEW_EXTENSIONS = new Set(['xlsx', 'xlsm', 'xltx', 'xltm'])
const TEXT_PREVIEW_EXTENSIONS = new Set(['txt', 'md', 'csv', 'json'])
const IMAGE_PREVIEW_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'])

const getExtensionFromDisposition = (contentDisposition) => {
  const raw = String(contentDisposition || '')
  if (!raw) return ''

  const utf8Match = raw.match(/filename\*=(?:UTF-8'')?([^;]+)/i)
  const simpleMatch = raw.match(/filename=([^;]+)/i)
  const fileNameToken = (utf8Match?.[1] || simpleMatch?.[1] || '').trim().replace(/^"|"$/g, '')
  if (!fileNameToken) return ''

  const safeFileName = decodeURIComponent(fileNameToken)
  return getExtension(safeFileName)
}

const getUrlExtension = (url) => {
  if (!url) return ''
  try {
    const normalizedUrl = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined)
    const pathParts = normalizedUrl.pathname.split('.')
    return pathParts.length > 1 ? String(pathParts.pop() || '').toLowerCase() : ''
  } catch {
    const normalized = String(url).split('?')[0]
    const pathParts = normalized.split('.')
    return pathParts.length > 1 ? String(pathParts.pop() || '').toLowerCase() : ''
  }
}

const hasZipSignature = (arrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer || new ArrayBuffer(0))
  return bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04
}

const toColumnLetter = (index) => {
  let col = ''
  let n = index
  while (n >= 0) {
    col = String.fromCharCode(65 + (n % 26)) + col
    n = Math.floor(n / 26) - 1
  }
  return col
}

const ArtifactPreviewPanel = ({ url, downloadMetadata, previewVersion, onClose }) => {
  const previewUrl = buildAgentUrl(url)
  if (!previewUrl) return null

  const EXCEL_ROW_HEIGHT = 34
  const EXCEL_OVERSCAN_ROWS = 16
  const extension = getUrlExtension(previewUrl)
  const [previewState, setPreviewState] = React.useState({
    status: 'idle',
    mode: 'none',
    error: '',
    excelSheets: [],
    activeSheetName: '',
    sheetName: '',
    headers: [],
    rows: [],
    colCount: 0,
    text: '',
    objectUrl: '',
  })
  const excelScrollRef = React.useRef(null)
  const [excelViewport, setExcelViewport] = React.useState({
    scrollTop: 0,
    height: 500,
  })

  const loadExcelData = async (arrayBuffer, metadata) => {
    try {
      const workbook = XLSX.read(arrayBuffer, { type: 'array' })

      const normalizeForSearch = (text) => {
        return String(text || '').trim().toLowerCase().replace(/[&\/\s]/g, '')
      }

      const normalizeLoose = (text) => {
        return String(text || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '')
      }

      const matchesRequestedSheet = (sheetName, requestedName) => {
        const normalizedSheet = normalizeLoose(sheetName)
        const normalizedRequested = normalizeLoose(requestedName)
        if (!normalizedSheet || !normalizedRequested) return false
        return (
          normalizedSheet === normalizedRequested ||
          normalizedSheet.includes(normalizedRequested) ||
          normalizedRequested.includes(normalizedSheet)
        )
      }

      const classifySheet = (sheetName) => {
        const normalized = normalizeForSearch(sheetName)
        const lower = String(sheetName || '').toLowerCase()
        
        if (lower.includes('census') || normalized.includes('census')) {
          return 'census'
        }
        if (lower.includes('p&l') || lower.includes('pnl') || normalized.includes('pnl')) {
          return 'pnl'
        }
        if (lower.includes('metadata') || lower.includes('metadat')) {
          return 'metadata'
        }
        return null
      }

      const sheetsByType = {
        census: null,
        pnl: null,
        metadata: null,
      }

      // Find sheets matching keywords
      for (const sheetName of workbook.SheetNames) {
        const type = classifySheet(sheetName)
        if (type && !sheetsByType[type]) {
          sheetsByType[type] = sheetName
        }
      }

      const metadataType = String(metadata?.type || '').trim().toLowerCase()
      const metadataSheetNames = Array.isArray(metadata?.sheet_names)
        ? metadata.sheet_names.filter((name) => typeof name === 'string' && name.trim())
        : []

      const selectedSheetNames = []
      const addSelectedSheet = (sheetName) => {
        if (!sheetName || selectedSheetNames.includes(sheetName)) return
        selectedSheetNames.push(sheetName)
      }

      if (metadataSheetNames.length && (metadataType === 'pnl' || metadataType === 'census' || metadataType === 'both')) {
        metadataSheetNames.forEach((requestedName) => {
          const matchedSheet = workbook.SheetNames.find((sheetName) => matchesRequestedSheet(sheetName, requestedName))
          if (matchedSheet) {
            addSelectedSheet(matchedSheet)
          }
        })
      }

      if (!selectedSheetNames.length) {
        if (metadataType === 'pnl' && sheetsByType.pnl) {
          addSelectedSheet(sheetsByType.pnl)
        } else if (metadataType === 'census' && sheetsByType.census) {
          addSelectedSheet(sheetsByType.census)
        } else if (metadataType === 'both') {
          addSelectedSheet(sheetsByType.pnl)
          addSelectedSheet(sheetsByType.census)
        } else {
          addSelectedSheet(sheetsByType.census)
          addSelectedSheet(sheetsByType.pnl)
        }
      }

      // Metadata sheet should always be displayed if present.
      addSelectedSheet(sheetsByType.metadata)

      if (!selectedSheetNames.length) {
        throw new Error('No supported sheet found. Expected Census, P&L, or Metadata.')
      }

      const sheets = selectedSheetNames.map((sheetName) => {
        const worksheet = workbook.Sheets[sheetName]
        const rawRef = worksheet?.['!ref'] || 'A1:A1'
        const decoded = XLSX.utils.decode_range(rawRef)

        // Keep column preview bounded for performance, but include all rows that contain values.
        const previewEndCol = Math.min(decoded.e.c, decoded.s.c + 49)
        const populatedRowIndexes = new Set<number>()

        Object.keys(worksheet || {}).forEach((cellAddress) => {
          if (!cellAddress || cellAddress.startsWith('!')) return

          const cell = worksheet[cellAddress]
          if (!cell) return

          const cellText =
            typeof cell.w === 'string'
              ? cell.w
              : cell.v == null
                ? ''
                : String(cell.v)

          if (!String(cellText).trim()) return

          try {
            const decodedCell = XLSX.utils.decode_cell(cellAddress)
            if (decodedCell.r >= decoded.s.r && decodedCell.r <= decoded.e.r) {
              populatedRowIndexes.add(decodedCell.r)
            }
          } catch {
            // Ignore non-cell worksheet keys.
          }
        })

        const sortedRowIndexes = Array.from(populatedRowIndexes).sort((a, b) => a - b)
        const rows = []

        sortedRowIndexes.forEach((r) => {
          const row = []
          for (let c = decoded.s.c; c <= previewEndCol; c += 1) {
            const cellAddress = XLSX.utils.encode_cell({ r, c })
            const cell = worksheet[cellAddress]

            if (!cell) {
              row.push('')
              continue
            }

            // Prefer XLSX display text (`w`) to preserve Excel formatting exactly in preview.
            if (typeof cell.w === 'string') {
              row.push(cell.w)
            } else {
              row.push(XLSX.utils.format_cell(cell) || '')
            }
          }
          rows.push(row)
        })

        const colCount = previewEndCol - decoded.s.c + 1
        return { sheetName, rows, colCount }
      })

      return { sheets }
    } catch (error) {
      console.error('Failed to load Excel data:', error)
      throw new Error(`Excel preview failed: ${error.message}`)
    }
  }

  const activeExcelSheet = React.useMemo(() => {
    if (!Array.isArray(previewState.excelSheets) || !previewState.excelSheets.length) {
      return { sheetName: '', rows: [], colCount: 0 }
    }

    return (
      previewState.excelSheets.find((sheet) => sheet.sheetName === previewState.activeSheetName) ||
      previewState.excelSheets[0]
    )
  }, [previewState.excelSheets, previewState.activeSheetName])

  React.useEffect(() => {
    if (previewState.mode !== 'excel') return
    const scrollEl = excelScrollRef.current
    if (!scrollEl) return

    const updateViewport = () => {
      setExcelViewport({
        scrollTop: scrollEl.scrollTop,
        height: scrollEl.clientHeight || 500,
      })
    }

    let resizeObserver = null
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updateViewport())
      resizeObserver.observe(scrollEl)
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [previewState.mode])

  React.useEffect(() => {
    if (previewState.mode !== 'excel') return
    const scrollEl = excelScrollRef.current
    if (!scrollEl) return

    scrollEl.scrollTop = 0
    setExcelViewport((prev) => ({
      ...prev,
      scrollTop: 0,
    }))
  }, [previewState.mode, previewState.activeSheetName])

  React.useEffect(() => {
    let isCanceled = false
    let localObjectUrl = ''

    const loadPreview = async () => {
      setPreviewState((prev) => ({ ...prev, status: 'loading', error: '', mode: 'none' }))

      try {
        const response = await fetch(previewUrl)
        if (!response.ok) {
          throw new Error(`Preview failed: ${response.status} ${response.statusText}`)
        }

        const contentType = String(response.headers.get('content-type') || '').toLowerCase()
        const contentDisposition = String(response.headers.get('content-disposition') || '')
        const extensionFromHeaders = getExtensionFromDisposition(contentDisposition)
        const resolvedExtension = extensionFromHeaders || extension
        const blob = await response.blob()
        const binaryBuffer = await blob.arrayBuffer()
        const looksLikeZipContainer = hasZipSignature(binaryBuffer)

        const shouldAttemptExcelParse =
          EXCEL_PREVIEW_EXTENSIONS.has(resolvedExtension) ||
          /spreadsheetml|ms-excel|officedocument\.spreadsheetml/.test(contentType) ||
          (!resolvedExtension && (/octet-stream|application\/zip/.test(contentType) || contentDisposition.toLowerCase().includes('.xlsx'))) ||
          looksLikeZipContainer

        if (shouldAttemptExcelParse) {
          try {
            const { sheets } = await loadExcelData(binaryBuffer, downloadMetadata)
            if (!isCanceled) {
              setPreviewState({
                status: 'ready',
                mode: 'excel',
                error: '',
                excelSheets: sheets,
                activeSheetName: sheets[0]?.sheetName || '',
                sheetName: sheets[0]?.sheetName || '',
                headers: [],
                rows: [],
                colCount: 0,
                text: '',
                objectUrl: '',
              })
            }
            return
          } catch (error) {
            console.error('Excel preview failed:', error)
            if (!isCanceled) {
              setPreviewState({
                status: 'error',
                mode: 'none',
                error: error.message || 'Unable to preview workbook.',
                excelSheets: [],
                activeSheetName: '',
                sheetName: '',
                headers: [],
                rows: [],
                colCount: 0,
                text: '',
                objectUrl: '',
              })
            }
            return
          }
        }

        const isTextLike =
          TEXT_PREVIEW_EXTENSIONS.has(resolvedExtension) ||
          contentType.startsWith('text/') ||
          /json|csv|xml|yaml/.test(contentType)

        if (isTextLike) {
          const text = await blob.text()

          // If we detect ZIP container bytes, this is binary content (often XLSX) and should not be shown as text.
          if (looksLikeZipContainer) {
            if (!isCanceled) {
              setPreviewState({
                status: 'ready',
                mode: 'none',
                error: '',
                excelSheets: [],
                activeSheetName: '',
                sheetName: '',
                headers: [],
                rows: [],
                colCount: 0,
                text: '',
                objectUrl: '',
              })
            }
            return
          }

          if (!isCanceled) {
            setPreviewState({
              status: 'ready',
              mode: 'text',
              error: '',
              excelSheets: [],
              activeSheetName: '',
              sheetName: '',
              headers: [],
              rows: [],
              colCount: 0,
              text: text.slice(0, 8000),
              objectUrl: '',
            })
          }
          return
        }

        const isImageLike = IMAGE_PREVIEW_EXTENSIONS.has(resolvedExtension) || contentType.startsWith('image/')
        const isPdfLike = resolvedExtension === 'pdf' || contentType.includes('application/pdf')

        if (isImageLike || isPdfLike) {
          localObjectUrl = URL.createObjectURL(blob)
          if (!isCanceled) {
            setPreviewState({
              status: 'ready',
              mode: isPdfLike ? 'pdf' : 'image',
              error: '',
              excelSheets: [],
              activeSheetName: '',
              sheetName: '',
              headers: [],
              rows: [],
              colCount: 0,
              text: '',
              objectUrl: localObjectUrl,
            })
          }
          return
        }

        if (!isCanceled) {
          setPreviewState({
            status: 'ready',
            mode: 'none',
            error: '',
            excelSheets: [],
            activeSheetName: '',
            sheetName: '',
            headers: [],
            rows: [],
            colCount: 0,
            text: '',
            objectUrl: '',
          })
        }
      } catch (error) {
        if (!isCanceled) {
          setPreviewState({
            status: 'error',
            mode: 'none',
            error: error.message || 'Unable to load preview.',
            excelSheets: [],
            activeSheetName: '',
            sheetName: '',
            headers: [],
            rows: [],
            colCount: 0,
            text: '',
            objectUrl: '',
          })
        }
      }
    }

    loadPreview()

    return () => {
      isCanceled = true
      if (localObjectUrl) {
        URL.revokeObjectURL(localObjectUrl)
      }
    }
  }, [previewUrl, extension, downloadMetadata, previewVersion])

  const totalExcelRows = activeExcelSheet.rows.length
  const safeViewportHeight = Math.max(excelViewport.height, EXCEL_ROW_HEIGHT)
  const visibleCount = Math.max(1, Math.ceil(safeViewportHeight / EXCEL_ROW_HEIGHT))
  const rawStart = Math.floor(excelViewport.scrollTop / EXCEL_ROW_HEIGHT)
  const visibleStart = Math.max(0, rawStart - EXCEL_OVERSCAN_ROWS)
  const visibleEnd = Math.min(totalExcelRows, rawStart + visibleCount + EXCEL_OVERSCAN_ROWS)
  const visibleRows = activeExcelSheet.rows.slice(visibleStart, visibleEnd)
  const topSpacerHeight = visibleStart * EXCEL_ROW_HEIGHT
  const bottomSpacerHeight = Math.max(0, (totalExcelRows - visibleEnd) * EXCEL_ROW_HEIGHT)

  return (
    <aside className="mt-4 lg:mt-0 rounded-2xl border border-emerald-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-[0_20px_60px_rgba(15,23,42,0.08)] overflow-hidden min-h-[420px] lg:sticky lg:top-8">
      <div className="border-b border-emerald-100 dark:border-gray-800 px-4 py-3 bg-white dark:bg-gray-900/80 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">Generated File Preview</h2>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Preview appears when the generated file is ready.</p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="ml-2 inline-flex items-center justify-center rounded-lg p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close preview"
            title="Close preview"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <div className="px-4 py-3 flex items-center gap-2 border-b border-emerald-100 dark:border-gray-800 bg-emerald-50/50 dark:bg-gray-800/40">
        {/* <a
          href={previewUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center rounded-lg border border-emerald-300 dark:border-emerald-500/30 px-3 py-1.5 text-xs font-semibold text-emerald-800 dark:text-emerald-200 hover:bg-emerald-100 dark:hover:bg-gray-700 transition-colors"
        >
          Open in new tab
        </a> */}
        <a
          href={previewUrl}
          download
          className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors"
        >
          Download
        </a>
      </div>

      <div className="h-[500px] bg-white dark:bg-gray-950 overflow-auto">
        {previewState.status === 'loading' && (
          <div className="h-full flex items-center justify-center px-6 text-center text-sm text-gray-600 dark:text-gray-300">
            Loading preview...
          </div>
        )}

        {previewState.status === 'error' && (
          <div className="h-full flex items-center justify-center px-6 text-center text-sm text-red-600 dark:text-red-300">
            {previewState.error}
          </div>
        )}

        {previewState.status === 'ready' && previewState.mode === 'excel' && (
          <div className="h-full flex flex-col overflow-hidden">
            {/* Formula bar */}
            <div className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/80 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-mono bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-2 py-0.5 text-[10px] min-w-[3rem] text-center">fx</span>
              <span className="truncate">{activeExcelSheet.sheetName} — {activeExcelSheet.rows.length} rows × {activeExcelSheet.colCount} columns</span>
              {/* <span className="ml-auto inline-flex items-center rounded border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300 whitespace-nowrap">
                debug T:{totalExcelRows} S:{visibleStart} E:{visibleEnd} R:{visibleRows.length}
              </span> */}
            </div>

            {/* Spreadsheet grid */}
            <div
              ref={excelScrollRef}
              onScroll={(event) => {
                const nextScrollTop = event.currentTarget?.scrollTop || 0
                setExcelViewport((prev) => ({
                  ...prev,
                  scrollTop: nextScrollTop,
                }))
              }}
              className="flex-1 overflow-auto"
            >
              <table className="border-collapse text-xs" style={{ minWidth: 'max-content', tableLayout: 'fixed' }}>
                <thead className="sticky top-0 z-10">
                  <tr>
                    {/* Corner cell */}
                    <th className="sticky left-0 z-20 w-10 min-w-[2.5rem] bg-gray-100 dark:bg-gray-800 border-b-2 border-r-2 border-gray-300 dark:border-gray-600 text-center text-[10px] text-gray-400 font-normal select-none" />
                    {Array.from({ length: activeExcelSheet.colCount }, (_, i) => (
                      <th
                        key={i}
                        className="bg-gray-100 dark:bg-gray-800 border-b-2 border-r border-gray-300 dark:border-gray-600 px-3 py-1.5 text-center text-[11px] text-gray-500 dark:text-gray-400 font-semibold min-w-[90px] select-none"
                      >
                        {toColumnLetter(i)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topSpacerHeight > 0 && (
                    <tr aria-hidden="true">
                      <td colSpan={Math.max(1, activeExcelSheet.colCount + 1)} style={{ height: `${topSpacerHeight}px`, padding: 0, border: 'none' }} />
                    </tr>
                  )}
                  {visibleRows.map((row, visibleIdx) => {
                    const rowIdx = visibleStart + visibleIdx
                    const isHeaderRow = rowIdx === 0
                    return (
                      <tr key={`row-${rowIdx}`} style={{ height: `${EXCEL_ROW_HEIGHT}px` }}>
                        {/* Row number */}
                        <td className="sticky left-0 z-10 w-10 bg-gray-100 dark:bg-gray-800 border-b border-r-2 border-gray-300 dark:border-gray-600 text-center text-[10px] text-gray-400 py-1.5 select-none font-normal">
                          {rowIdx + 1}
                        </td>
                        {Array.from({ length: activeExcelSheet.colCount }, (_, colIdx) => (
                          <td
                            key={colIdx}
                            className={[
                              'border-b border-r px-2 py-1.5 whitespace-nowrap overflow-hidden text-ellipsis max-w-[180px]',
                              isHeaderRow
                                ? 'bg-[#cfe2f3] dark:bg-blue-950/50 text-gray-900 dark:text-gray-100 font-semibold text-center border-gray-300 dark:border-gray-600'
                                : rowIdx % 2 === 0
                                  ? 'bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700'
                                  : 'bg-[#ebf5fb] dark:bg-gray-800/60 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700',
                            ].join(' ')}
                          >
                            {String(Array.isArray(row) ? (row[colIdx] ?? '') : '')}
                          </td>
                        ))}
                      </tr>
                    )
                  })}
                  {bottomSpacerHeight > 0 && (
                    <tr aria-hidden="true">
                      <td colSpan={Math.max(1, activeExcelSheet.colCount + 1)} style={{ height: `${bottomSpacerHeight}px`, padding: 0, border: 'none' }} />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Sheet tab at bottom */}
            <div className="flex-shrink-0 flex items-center gap-2 px-3 py-1 border-t-2 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-900/80 overflow-x-auto">
              {previewState.excelSheets.map((sheet) => {
                const isActive = sheet.sheetName === activeExcelSheet.sheetName
                return (
                  <button
                    key={sheet.sheetName}
                    type="button"
                    onClick={() => {
                      if (!isActive) {
                        setPreviewState((prev) => ({
                          ...prev,
                          activeSheetName: sheet.sheetName,
                          sheetName: sheet.sheetName,
                        }))
                      }
                    }}
                    className={[
                      'inline-flex items-center px-4 py-1 rounded-t text-[11px] font-medium border border-b-0 -mb-1 shadow-sm whitespace-nowrap transition-colors',
                      isActive
                        ? 'text-emerald-800 dark:text-emerald-300 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                        : 'text-gray-600 dark:text-gray-300 bg-gray-200/80 dark:bg-gray-800/80 border-gray-300 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800',
                    ].join(' ')}
                  >
                    {sheet.sheetName}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {previewState.status === 'ready' && previewState.mode === 'text' && (
          <pre className="h-full m-0 p-3 text-xs sm:text-sm whitespace-pre-wrap break-words text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-950">
            {previewState.text}
          </pre>
        )}

        {previewState.status === 'ready' && previewState.mode === 'image' && previewState.objectUrl && (
          <div className="h-full flex items-center justify-center p-3">
            <img src={previewState.objectUrl} alt="Generated artifact preview" className="max-w-full max-h-full object-contain" />
          </div>
        )}

        {previewState.status === 'ready' && previewState.mode === 'pdf' && previewState.objectUrl && (
          <object data={previewState.objectUrl} type="application/pdf" className="w-full h-full">
            <div className="h-full flex items-center justify-center px-6 text-center text-sm text-gray-600 dark:text-gray-300">
              PDF preview is unavailable in this browser. Use "Open in new tab".
            </div>
          </object>
        )}

        {previewState.status === 'ready' && previewState.mode === 'none' && (
          <div className="h-full flex items-center justify-center px-6 text-center text-sm text-gray-600 dark:text-gray-300">
            Inline preview is unavailable for this file type. Use "Open in new tab".
          </div>
        )}
      </div>
    </aside>
  )
}

const mapBodySchemaWithChoice = (schema, chosenValue) => {
  if (Array.isArray(schema)) {
    return schema.map((item) => mapBodySchemaWithChoice(item, chosenValue))
  }

  if (schema && typeof schema === 'object') {
    return Object.entries(schema).reduce((acc, [key, value]) => {
      acc[key] = mapBodySchemaWithChoice(value, chosenValue)
      return acc
    }, {})
  }

  if (schema === '<chosen value>') {
    return chosenValue
  }

  return schema
}
const renderInlineMarkdown = (text, keyPrefix) => {
  // Matches **bold**, *italic-as-bold*, or _italic-as-bold_ patterns
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g)
  return parts.map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <strong key={`${keyPrefix}-b-${i}`} className="font-bold text-gray-900 dark:text-gray-100">{part.slice(2, -2)}</strong>
    }
    if (/^\*[^*]+\*$/.test(part) || /^_[^_]+_$/.test(part)) {
      return <strong key={`${keyPrefix}-b-${i}`} className="font-semibold text-gray-900 dark:text-gray-100">{part.slice(1, -1)}</strong>
    }
    return part
  })
}

const SingleChoicePromptMessage = ({ payload, onSelect }) => {
  const { title, message, options = [] } = payload || {}
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  if (!payload || !Array.isArray(options) || !options.length) return null

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title || 'Select an option'}</h3>
      {message && <p className="text-sm text-gray-700 dark:text-gray-300">{renderInlineMarkdown(message, 'prompt-message')}</p>}
      <div role="radiogroup" className="space-y-2">
        {options.map((option, index) => {
          const optionValue = String(option?.value ?? '')
          const optionLabel = String(option?.label ?? optionValue)

          return (
            <label
              key={`${optionValue}-${index}`}
              className={[
                'flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors',
                isSubmitting
                  ? 'cursor-not-allowed border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800/70'
                  : 'cursor-pointer border-emerald-200 bg-emerald-50/60 hover:border-emerald-300 dark:border-gray-700 dark:bg-gray-800/70',
              ].join(' ')}
            >
              <input
                type="radio"
                name={`waiting-input-${payload.step || payload.field || 'choice'}`}
                value={optionValue}
                disabled={isSubmitting}
                onChange={() => {
                  if (!isSubmitting) {
                    setIsSubmitting(true)
                    Promise.resolve(onSelect(optionValue, optionLabel)).finally(() => {
                      setIsSubmitting(false)
                    })
                  }
                }}
                className="h-4 w-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
              />
              <span className="text-sm text-gray-800 dark:text-gray-200">{optionLabel}</span>
            </label>
          )
        })}
      </div>
      {isSubmitting && <p className="text-xs text-gray-500 dark:text-gray-400">Submitting your selection...</p>}
    </div>
  )
}

const isMarkdownTableDivider = (line) => /^\|\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(line.trim())

const isMarkdownTableRow = (line) => /^\|.*\|$/.test(line.trim())

const parseMarkdownTableRow = (line) =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim())

const renderMarkdownTableCellContent = (value, keyPrefix, fallback = '') => {
  const text = String(value ?? '').trim()
  if (!text) return fallback

  const parts = text.split(/<br\s*\/?\s*>/gi)
  return parts.map((part, idx) => (
    <React.Fragment key={`${keyPrefix}-part-${idx}`}>
      {renderInlineMarkdown(part, `${keyPrefix}-inline-${idx}`)}
      {idx < parts.length - 1 && <br />}
    </React.Fragment>
  ))
}



const renderMessageWithMarkdownTables = (text) => {
  const lines = String(text || '').split(/\r?\n/)
  const blocks = []
  let index = 0

  while (index < lines.length) {
    const currentLine = lines[index]
    const trimmedLine = currentLine.trim()

    if (!trimmedLine) {
      index += 1
      continue
    }

    // Look for table start: a row line followed by a divider (with possible blank lines in between)
    if (isMarkdownTableRow(trimmedLine)) {
      let dividerIndex = index + 1

      // Skip blank lines to find the divider
      while (dividerIndex < lines.length && !lines[dividerIndex].trim()) {
        dividerIndex++
      }

      // Check if we found a divider
      if (dividerIndex < lines.length && isMarkdownTableDivider(lines[dividerIndex].trim())) {
        const headerCells = parseMarkdownTableRow(trimmedLine)
        const rows = []
        index = dividerIndex + 1

        // Collect all rows, allowing blank lines
        while (index < lines.length) {
          const line = lines[index]
          const trimmedTableLine = line.trim()

          if (!trimmedTableLine) {
            // Skip blank lines within table
            index++
            continue
          }

          if (isMarkdownTableRow(trimmedTableLine)) {
            rows.push(parseMarkdownTableRow(trimmedTableLine))
            index++
          } else {
            // Stop when we hit non-table content
            break
          }
        }

        blocks.push(
          <div key={`table-${index}`} className="overflow-x-auto rounded-xl border border-emerald-100 dark:border-gray-700 bg-white dark:bg-gray-800/70">
            <table className="min-w-full text-xs sm:text-sm">
              <thead className="bg-emerald-50 dark:bg-gray-800 text-emerald-900 dark:text-emerald-100">
                <tr>
                  {headerCells.map((cell, cellIndex) => (
                    <th
                      key={`table-header-${index}-${cellIndex}`}
                      className="px-3 py-2 text-left font-semibold border-b border-emerald-100 dark:border-gray-700"
                    >
                      {renderMarkdownTableCellContent(cell, `table-header-${index}-${cellIndex}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr key={`table-row-${index}-${rowIndex}`} className="odd:bg-white even:bg-emerald-50/40 dark:odd:bg-gray-800/70 dark:even:bg-gray-700/50">
                    {headerCells.map((_, cellIndex) => (
                      <td
                        key={`table-cell-${index}-${rowIndex}-${cellIndex}`}
                        className="px-3 py-2 border-b border-emerald-100 dark:border-gray-700 text-gray-700 dark:text-gray-200 align-top"
                      >
                        {renderMarkdownTableCellContent(row[cellIndex], `table-cell-${index}-${rowIndex}-${cellIndex}`, '-')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
        continue
      }
    }

    // Heading lines: # H1, ## H2, ### H3
    const headingMatch = trimmedLine.match(/^(#{1,3})\s+(.+)$/)
    if (headingMatch) {
      const level = headingMatch[1].length
      const headingText = headingMatch[2]
      const headingClass =
        level === 1
          ? 'text-base font-bold text-gray-900 dark:text-gray-100 mt-3 mb-1'
          : level === 2
            ? 'text-sm font-bold text-gray-800 dark:text-gray-200 mt-2 mb-1'
            : 'text-sm font-semibold text-gray-700 dark:text-gray-300 mt-1 mb-0.5'
      blocks.push(
        <p key={`heading-${index}`} className={headingClass}>
          {renderInlineMarkdown(headingText, `heading-${index}`)}
        </p>
      )
      index += 1
      continue
    }

    // Not a table, collect as paragraph
    const paragraphLines = [currentLine]
    index += 1

    while (
      index < lines.length &&
      lines[index].trim() &&
      !isMarkdownTableRow(lines[index].trim()) &&
      !/^#{1,3}\s+/.test(lines[index].trim())
    ) {
      paragraphLines.push(lines[index])
      index += 1
    }

    blocks.push(
      <p key={`paragraph-${index}`} className="whitespace-pre-wrap">
        {paragraphLines.map((line, lineIdx) => (
          <React.Fragment key={lineIdx}>
            {lineIdx > 0 && '\n'}
            {renderInlineMarkdown(line, `paragraph-${index}-${lineIdx}`)}
          </React.Fragment>
        ))}
      </p>
    )
  }

  return blocks
}

const formatBytes = (bytes) => {
  const size = Number(bytes || 0)
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(2)} MB`
}

const getExtension = (fileName) => {
  const parts = String(fileName || '').toLowerCase().split('.')
  return parts.length > 1 ? parts.pop() : ''
}

const DATA_FORMATER_AGENT_HOST = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const INTRO_MESSAGE = 'Upload a file and I will read it, provide a concise summary, and then answer your follow-up questions in this chat.'

const sendMessageToApi = async ({ message, jobId }) => {
  const apiUrl = `${DATA_FORMATER_AGENT_HOST}/api/v1/data-formater-agent/chat/message`
  const formData = new FormData()
  formData.append('message', message)
  formData.append('provider', 'anthropic')
  if (jobId) {
    formData.append('job_id', jobId)
  }

  const response = await fetch(apiUrl, {
    method: 'POST',
    body: formData,
  })

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`)
  }

  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    const rawText = (await response.text()).trim()
    return rawText || 'Message sent successfully, but no response body was returned.'
  }

  const data = await response.json()
  const responseText =
    data?.answer ||
    data?.message ||
    data?.response ||
    data?.content ||
    data?.data?.message ||
    data?.data?.response ||
    data?.data?.content

  return typeof responseText === 'string' && responseText.trim()
    ? responseText
    : 'Message sent successfully, but no assistant reply text was found in the response.'
}

const streamAgentAnalysis = async (
  jobId,
  onEstimationMessage,
  onProgressMessage,
  onAgentMessage,
  onReviewRequired,
  onError,
  activeStreamRef,
  processedEventsRef,
  onDownloadReady,
  onWaitingForInput
) => {
  const emitAgentMessage = (data) => {
    if (data.download_url) {
      onEstimationMessage('')
      onDownloadReady(data.download_url, data.download_metadata || null)
    }

    const eventKey = JSON.stringify(data)
    if (processedEventsRef?.current?.jobId !== jobId) {
      processedEventsRef.current = {
        jobId,
        keys: new Set(),
      }
    }

    if (processedEventsRef?.current?.keys?.has(eventKey)) {
      return false
    }

    processedEventsRef?.current?.keys?.add(eventKey)

    if (data.type === 'progress' && data.message) {
      onProgressMessage(data.message)
      return true
    }

    if (data.type === 'classification_estimate' && data.message) {
      onEstimationMessage(data.message)
      return true
    }

    if (data.type === 'waiting_for_input') {
      onEstimationMessage('')

      if (data.input_kind === 'single_choice' && Array.isArray(data.options) && data.options.length > 0) {
        onWaitingForInput?.(data)
        return true
      }

      if (data.message) {
        onAgentMessage(data.message)
      }
      onWaitingForInput?.(data)
      return true
    }

    if (data.type === 'review_required') {
      onReviewRequired(data.payload)
      return true
    }

    // if (data.message) {
    //   onAgentMessage(data.message)
    //   return true
    // }

    // if (data.type === 'connected' && data.job_id) {
    //   onAgentMessage(`Connected to analysis stream (job: ${data.job_id}).`)
    //   return true
    // }

    // if (data.type === 'status_snapshot' && data.status) {
    //   onAgentMessage(`Current status: ${data.status}.`)
    //   return true
    // }

    return true
  }

  try {
    if (typeof window === 'undefined' || !window.EventSource) {
      throw new Error('EventSource is not supported in this browser.')
    }

    const streamUrl = `${DATA_FORMATER_AGENT_HOST}/api/v1/data-formater-agent/chat/stream/${encodeURIComponent(jobId)}`
    console.log('Starting stream from:', streamUrl)

    await new Promise<void>((resolve, reject) => {
      let isResolved = false

      if (activeStreamRef?.current) {
        activeStreamRef.current.close()
        activeStreamRef.current = null
      }

      const eventSource = new EventSource(streamUrl)
      if (activeStreamRef) {
        activeStreamRef.current = eventSource
      }

      const closeAndResolve = () => {
        if (isResolved) return
        isResolved = true
        eventSource.close()
        if (activeStreamRef?.current === eventSource) {
          activeStreamRef.current = null
        }
        resolve()
      }

      const closeAndReject = (err) => {
        if (isResolved) return
        isResolved = true
        eventSource.close()
        if (activeStreamRef?.current === eventSource) {
          activeStreamRef.current = null
        }
        reject(err)
      }

      eventSource.addEventListener('agent_update', (event) => {
        try {
          const data = JSON.parse(event.data)
          const isNewEvent = emitAgentMessage(data)

          if (
            isNewEvent && (
            data.type === 'complete'
            )
          ) {
            closeAndResolve()
          }
        } catch (parseErr) {
          console.error('Failed to parse agent_update payload:', parseErr, event.data)
        }
      })

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          emitAgentMessage(data)
        } catch {
          // Ignore non-JSON keep-alive frames.
        }
      }

      eventSource.onerror = () => {
        closeAndReject(
          new Error(`Unable to open or maintain SSE connection. URL: ${streamUrl}`)
        )
      }
    })
  } catch (error) {
    console.error('Stream analysis failed:', error)
    const errorMsg =
      error instanceof TypeError && error.message === 'Failed to fetch'
        ? 'Unable to open analysis stream from browser. This is likely a CORS restriction on the stream endpoint.'
        : error.message || 'Agent analysis streaming failed.'
    onError(errorMsg)
  }
}

const ContributeRawDataPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false)
  const [messages, setMessages] = React.useState([])
  const [inputValue, setInputValue] = React.useState('')
  const [isAnalyzing, setIsAnalyzing] = React.useState(false)
  const [isResponding, setIsResponding] = React.useState(false)
  const [fileError, setFileError] = React.useState('')
  const [uploadedFile, setUploadedFile] = React.useState(null)
  const [jobId, setJobId] = React.useState(null)
  const [estimation, setEstimation] = React.useState('')
  const [currentStatus, setCurrentStatus] = React.useState('')
const [isDragActive, setIsDragActive] = React.useState(false)
  const [isAwaitingReview, setIsAwaitingReview] = React.useState(false)
  const [artifactPreviewUrl, setArtifactPreviewUrl] = React.useState('')
  const [artifactDownloadMetadata, setArtifactDownloadMetadata] = React.useState(null)
  const [artifactPreviewVersion, setArtifactPreviewVersion] = React.useState(0)
  const [previewWidth, setPreviewWidth] = React.useState(420)
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(true)

  const fileInputRef = React.useRef(null)
  const messagesEndRef = React.useRef(null)
  const activeStreamRef = React.useRef(null)
  const processedEventsRef = React.useRef({ jobId: null, keys: new Set() })
  const jobIdRef = React.useRef(null)
  const previewResizeRef = React.useRef({ isActive: false, startX: 0, startWidth: 420 })
  const previewResizeRafRef = React.useRef(null)
  const hasPreview = Boolean(artifactPreviewUrl)
  const isInputEnabled = Boolean(uploadedFile) && isAwaitingReview && !isResponding && !isAnalyzing

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isResponding, isAnalyzing])

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsSidebarOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  React.useEffect(() => {
    return () => {
      if (activeStreamRef.current) {
        activeStreamRef.current.close()
        activeStreamRef.current = null
      }
    }
  }, [])

  React.useEffect(() => {
    const handleMouseMove = (event) => {
      if (!previewResizeRef.current.isActive) return
      const delta = previewResizeRef.current.startX - event.clientX
      const nextWidth = Math.min(760, Math.max(300, previewResizeRef.current.startWidth + delta))

      if (previewResizeRafRef.current) {
        cancelAnimationFrame(previewResizeRafRef.current)
      }

      previewResizeRafRef.current = requestAnimationFrame(() => {
        setPreviewWidth(nextWidth)
        previewResizeRafRef.current = null
      })
    }

    const handleMouseUp = () => {
      if (previewResizeRef.current.isActive) {
        previewResizeRef.current.isActive = false
      }
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      if (previewResizeRafRef.current) {
        cancelAnimationFrame(previewResizeRafRef.current)
        previewResizeRafRef.current = null
      }
    }
  }, [])

  const appendAssistantMessage = (content) => {
    setMessages((prev) => [...prev, { role: 'assistant', content }])
  }

  const appendUserMessage = (content) => {
    setMessages((prev) => [...prev, { role: 'user', content }])
  }

  const updateCurrentStatus = (content) => {
    if (typeof content !== 'string') return
    const nextStatus = content.trim()
    if (!nextStatus) return
    setCurrentStatus(nextStatus)
  }

  const updateEstimation = (content) => {
    setEstimation(typeof content === 'string' ? content : '')
  }

  const submitWaitingInputChoice = async (payload, selectedValue, selectedLabel) => {
    const endpoint = payload?.submit?.endpoint
    const method = String(payload?.submit?.method || 'POST').toUpperCase()
    const selectedText = String(selectedValue || '').trim()
    const displayText = String(selectedLabel || selectedValue || '').trim()

    if (!endpoint) {
      if (!selectedText) {
        appendAssistantMessage('Unable to submit selection: selected value is empty.')
        return
      }

      appendUserMessage(displayText || selectedText)
      setIsAwaitingReview(false)

      const currentJobId = jobIdRef.current || jobId
      if (!currentJobId) {
        appendAssistantMessage('Selection captured, but no active analysis job was found to continue the chat flow.')
        return
      }

      try {
        const answer = await sendMessageToApi({
          message: selectedText,
          jobId: currentJobId,
        })

        if (answer) {
          appendAssistantMessage(answer)
        }
      } catch (error) {
        appendAssistantMessage(`⚠️ Failed to send selection as chat message: ${error.message || 'Unknown error.'}`)
      }

      return
    }

    const submitUrl = /^https?:\/\//i.test(endpoint)
      ? endpoint
      : `${DATA_FORMATER_AGENT_HOST}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`

    const bodySchema = payload?.submit?.body_schema || {}
    const requestBody = mapBodySchemaWithChoice(bodySchema, selectedValue)

    try {
      const response = await fetch(submitUrl, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        throw new Error(`Input submit failed: ${response.status} ${response.statusText}`)
      }

      appendUserMessage(selectedLabel || selectedValue)
      setIsAwaitingReview(false)
      appendAssistantMessage('Selection submitted successfully.')
    } catch (error) {
      appendAssistantMessage(`⚠️ Failed to submit selection: ${error.message || 'Unknown error.'}`)
    }
  }

  const uploadFileToApi = async (file) => {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('message', 'Please map this workbook into guidance template')
      formData.append('provider', 'anthropic')

      const apiUrl = `${DATA_FORMATER_AGENT_HOST}/api/v1/data-formater-agent/chat/upload`

      console.log('Uploading to:', apiUrl)

      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      if (data.job_id) {
        setJobId(data.job_id)
        jobIdRef.current = data.job_id
        console.log('File uploaded successfully. Job ID:', data.job_id)
        return data.job_id
      } else {
        throw new Error('No job_id in response')
      }
    } catch (error) {
      console.error('API upload failed:', error)
      const errorMsg =
        error instanceof TypeError && error.message === 'Failed to fetch'
          ? 'Network error: Unable to reach the upload service. Please check your connection.'
          : error.message || 'Upload to API failed. Please try again.'
      throw new Error(errorMsg)
    }
  }

  const handleFileUpload = async (file) => {
    if (!file) return

    setFileError('')
    setIsAnalyzing(true)
    setIsAwaitingReview(false)
    setCurrentStatus('Uploading file and starting workbook analysis...')
    processedEventsRef.current = { jobId: null, keys: new Set() }

    setUploadedFile({
      name: file.name,
      size: file.size,
      type: file.type,
    })

    try {
      const uploadedJobId = await uploadFileToApi(file)

      setIsAnalyzing(false)
      updateCurrentStatus('Starting workbook analysis...')

      await streamAgentAnalysis(
        uploadedJobId,
        (message) => {
          updateEstimation(message)
        },
        (message) => {
          updateCurrentStatus(message)
        },
        (message) => {
          appendAssistantMessage(message)
        },
        (payload) => {
          const reviewComponent = <ReviewPayloadComponent payload={payload} />
          setMessages((prev) => [...prev, { role: 'assistant', content: reviewComponent }])
        },
        (error) => {
          appendAssistantMessage(`⚠️ Analysis error: ${error}`)
        },
        activeStreamRef,
        processedEventsRef,
        (downloadUrl, downloadMetadata) => {
          setArtifactPreviewUrl(downloadUrl)
          setArtifactDownloadMetadata(downloadMetadata || null)
          setArtifactPreviewVersion((prev) => prev + 1)
          setMessages((prev) => [...prev, { role: 'assistant', content: <DownloadArtifactMessage url={downloadUrl} /> }])
        },
        (payload) => {
          if (payload?.input_kind === 'single_choice' && Array.isArray(payload?.options) && payload.options.length > 0) {
            setIsAwaitingReview(true)
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content: (
                  <SingleChoicePromptMessage
                    payload={payload}
                    onSelect={(selectedValue, selectedLabel) => submitWaitingInputChoice(payload, selectedValue, selectedLabel)}
                  />
                ),
              },
            ])
            return
          }

          setIsAwaitingReview(true)
        }
      )
    } catch (apiError) {
      console.error('API upload error:', apiError)
      setFileError(apiError.message)
      setEstimation('')
      appendAssistantMessage(`⚠️ Upload failed: ${apiError.message}`)
      setIsAnalyzing(false)
    }
  }

  const handleSelectFile = (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    handleFileUpload(file)
    event.target.value = ''
  }

  const handleSend = async () => {
    const question = inputValue.trim()
    if (!isInputEnabled || !question || isResponding || isAnalyzing) return

    appendUserMessage(question)
    setInputValue('')
    setIsAwaitingReview(false)

    if (!uploadedFile) {
      appendAssistantMessage('Please upload a file first, then I can summarize it and answer questions.')
      return
    }

    if (!jobId) {
      appendAssistantMessage('No active analysis job found. Please upload the file again before sending follow-up questions.')
      return
    }

    setIsResponding(true)

    try {
      const answer = await sendMessageToApi({
        message: question,
        jobId,
      })

      if (answer) {
        appendAssistantMessage(answer)
      }
    } catch (apiError) {
      console.error('Send message API failed:', apiError)
      appendAssistantMessage(`⚠️ Message send failed: ${apiError.message || 'Unable to send message.'}`)
    } finally {
      setIsResponding(false)
    }
  }

  const handleInputKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSend()
    }
  }

  const openFilePicker = () => {
    fileInputRef.current?.click()
  }

  const handleDrop = (event) => {
    event.preventDefault()
    setIsDragActive(false)

    const file = event.dataTransfer?.files?.[0]
    if (file) {
      handleFileUpload(file)
    }
  }

  const startPreviewResize = (event) => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) return
    event.preventDefault()
    previewResizeRef.current = {
      isActive: true,
      startX: event.clientX,
      startWidth: previewWidth,
    }
  }

  return (
    <section className="min-h-screen bg-[#f4fbf6] dark:bg-gray-950 relative overflow-hidden py-8 px-4 sm:px-6">

      <div className="pointer-events-none absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-emerald-200/30 blur-3xl" />
      <div className="pointer-events-none absolute -top-40 -right-32 w-[440px] h-[440px] rounded-full bg-sky-200/25 blur-3xl" />
      <div className="pointer-events-none absolute bottom-40 right-1/4 w-[380px] h-[380px] rounded-full bg-indigo-200/20 blur-3xl" />

      <div className={[
        'relative z-10 max-w-7xl mx-auto',
        hasPreview ? 'lg:flex lg:items-start lg:gap-4' : '',
      ].join(' ')}>
        <div className="rounded-2xl border border-emerald-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-[0_20px_60px_rgba(15,23,42,0.08)] overflow-hidden lg:flex-1 lg:min-w-0">
        <header className="border-b border-emerald-100 dark:border-gray-800 px-5 sm:px-6 py-4 bg-white dark:bg-gray-900/70">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-green-600 dark:text-gray-100 tracking-tight">
                Contribute Raw Data Assistant
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Chat-first flow: upload once, get instant summary, then ask follow-up questions.
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleSelectFile}
                accept=".txt,.md,.csv,.json,.xlsx"
              />
              <button
                type="button"
                onClick={openFilePicker}
                className="inline-flex items-center gap-2 rounded-full border border-emerald-300 dark:border-emerald-500/30 bg-emerald-50 dark:bg-gray-800 px-4 py-2 text-sm font-medium text-emerald-800 dark:text-gray-100 hover:bg-emerald-100 dark:hover:bg-gray-700 transition-colors"
              >
                <span aria-hidden="true">+</span>
                Upload File
              </button>
              {hasPreview && !isPreviewOpen && (
                <button
                  type="button"
                  onClick={() => setIsPreviewOpen(true)}
                  className="inline-flex items-center justify-center rounded-lg bg-emerald-600 dark:bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 dark:hover:bg-emerald-600 transition-colors shadow-md"
                >
                  <span className="mr-2">📂</span>
                  Reopen Preview
                </button>
              )}
            </div>
          </div>

          {uploadedFile && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-emerald-50 dark:bg-gray-800 px-3 py-1.5 text-xs text-emerald-800 dark:text-gray-300 border border-emerald-100 dark:border-gray-700">
              <span className="font-semibold">Loaded:</span>
              <span>{uploadedFile.name}</span>
              <span className="opacity-70">({formatBytes(uploadedFile.size)})</span>
            </div>
          )}
        </header>

        <div
          className={[
            'px-4 sm:px-6 h-[58vh] overflow-y-auto space-y-4 bg-gradient-to-b from-emerald-50/40 to-white dark:from-gray-900 dark:to-gray-900',
            currentStatus ? 'pt-0 pb-4' : 'py-4',
          ].join(' ')}
        >
          {currentStatus && (
            <div className="sticky top-0 z-10 rounded-xl border border-emerald-200 bg-emerald-50/95 px-3 py-2 text-xs text-emerald-900 shadow-sm backdrop-blur dark:border-emerald-500/30 dark:bg-emerald-950/85 dark:text-emerald-100">
              <p className="mb-2 text-[11px] font-semibold text-emerald-700 dark:text-emerald-200">{estimation}</p>
              <div className="flex items-start gap-2">
                <span className="mt-1 inline-flex h-2 w-2 flex-shrink-0 rounded-full bg-emerald-500 animate-pulse" />
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">Current status</p>
                  <p className="mt-0.5 leading-5">{currentStatus}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-start">
            <div className="max-w-[92%] sm:max-w-[80%] rounded-2xl rounded-bl-md px-4 py-3 shadow-sm text-sm leading-6 whitespace-pre-wrap bg-white dark:bg-gray-800 border border-emerald-100 dark:border-gray-700 text-gray-800 dark:text-gray-100">
              {INTRO_MESSAGE}
            </div>
          </div>

          {messages.map((message, index) => (
            <div key={`${message.role}-${index}`} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={[
                  'rounded-2xl px-4 py-3 shadow-sm text-sm leading-6',
                  message.role === 'user'
                    ? 'max-w-[92%] sm:max-w-[80%] whitespace-pre-wrap bg-gradient-to-r from-emerald-700 to-indigo-700 text-white rounded-br-md'
                    : typeof message.content === 'string'
                      ? 'max-w-[92%] sm:max-w-[80%] whitespace-pre-wrap bg-white dark:bg-gray-800 border border-emerald-100 dark:border-gray-700 text-gray-800 dark:text-gray-100 rounded-bl-md'
                      : 'max-w-[96%] sm:max-w-[90%] bg-white dark:bg-gray-800 border border-emerald-100 dark:border-gray-700 text-gray-800 dark:text-gray-100 rounded-bl-md overflow-x-auto',
                ].join(' ')}
              >
                {typeof message.content === 'string' && message.role === 'assistant'
                  ? renderMessageWithMarkdownTables(message.content)
                  : message.content}
              </div>
            </div>
          ))}
          

          {(isAnalyzing || isResponding) && (
            <div className="flex justify-start">
              <div className="inline-flex items-center gap-2 rounded-2xl rounded-bl-md px-4 py-3 bg-white dark:bg-gray-800 border border-emerald-100 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-sm">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span>{isAnalyzing ? 'Analyzing your file...' : 'Thinking...'}</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <footer className="border-t border-emerald-100 dark:border-gray-800 bg-white dark:bg-gray-900/80 p-4 sm:p-5 sticky bottom-0">
          <div className="flex gap-3 items-center justify-between">
            <textarea
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={handleInputKeyDown}
              disabled={!isInputEnabled}
              placeholder={isAwaitingReview ? 'Reply to the agent review request...' : 'Input unlocks when the assistant asks for it...'}
              rows={2}
              className="flex-1 resize-none rounded-xl border border-emerald-200 dark:border-gray-700 bg-emerald-50/80 dark:bg-gray-800 px-4 py-3 text-sm text-gray-800 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-60"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!isInputEnabled || !inputValue.trim() || isResponding || isAnalyzing}
              className="inline-flex items-center justify-center gap-2 h-12 px-6 flex-shrink-0 rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
              title="Send message (Enter)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
              <span>Send</span>
            </button>
          </div>
        </footer>
        </div>

        {hasPreview && isPreviewOpen && (
          <div className="relative mt-4 lg:mt-0 w-full lg:flex-shrink-0" style={{ width: `min(100%, ${previewWidth}px)` }}>
            <button
              type="button"
              onMouseDown={startPreviewResize}
              className="hidden lg:flex absolute -left-2 top-10 bottom-4 z-20 w-4 items-center justify-center cursor-col-resize group"
              aria-label="Resize preview panel"
              title="Drag to resize preview panel"
            >
              <span className="h-24 w-1 rounded-full bg-emerald-200 dark:bg-gray-700 group-hover:bg-emerald-400 dark:group-hover:bg-emerald-500 transition-colors" />
            </button>
            <ArtifactPreviewPanel
              url={artifactPreviewUrl}
              downloadMetadata={artifactDownloadMetadata}
              previewVersion={artifactPreviewVersion}
              onClose={() => setIsPreviewOpen(false)}
            />
          </div>
        )}
      </div>
    </section>
  )
}

export default ContributeRawDataPage